import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import type { AddressDetails } from "./AddressForm";
import { Breadcrumb, BreadcrumbItem } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import mapboxgl from "mapbox-gl";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { useAuth } from "../../contexts/AuthContext";
import { useAddress } from "../../contexts/AddressContext";
import { useContract } from "../../contexts/ContractContext";
import {
  saveAddress,
  type AddressData,
} from "../../services/addresses";
import LocationMap from "./LocationMap";
import AddressForm from "./AddressForm";
import LocationActions from "./LocationActions";

// Set access token
const accessToken = import.meta.env["VITE_MAPBOX_ACCESS_TOKEN"];
if (!accessToken) {
  console.error("Mapbox access token is missing. Please check your .env file");
} else {
  try {
    mapboxgl.accessToken = accessToken;
    console.log("Mapbox access token successfully initialized");
  } catch (error) {
    console.error("Error initializing Mapbox access token:", error);
  }
}

const LocationPage: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const { addressDetails, setAddressDetails } = useAddress();
  const { setTempAddress } = useContract();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // If the name is "addressDetails", replace the entire addressDetails object
    if (name === "addressDetails") {
      setAddressDetails(value as unknown as AddressDetails);
    } else {
      // Otherwise, update just the specified property
      const updatedDetails = {
        ...addressDetails,
        [name]: value,
      };
      setAddressDetails(updatedDetails);
    }
  };

  const handleSave = async () => {
    try {
      if (!user) {
        setSaveStatus(
          "Fehler: Sie müssen angemeldet sein, um Adressen zu speichern"
        );
        return;
      }

      if (isSaving) return; // double-click guard

      if (!currentLocation) {
        setSaveStatus("Fehler: Kein Standort ausgewählt");
        return;
      }

      setIsSaving(true);
      setSaveStatus("Wird gespeichert...");

      const locationData: AddressData = {
        street: addressDetails.streetName,
        house_number: addressDetails.streetNumber,
        zip_code: addressDetails.zipcode,
        city: addressDetails.city,
        country: addressDetails.country,
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        created_by_user_id: user.id,
      };

      // Single atomic call — DB upsert_address handles deduplication
      const savedAddress = await saveAddress(locationData);
      setSaveStatus("Standort erfolgreich gespeichert!");

      setTempAddress({
        id: savedAddress.id,
        street: savedAddress.street,
        house_number: savedAddress.house_number,
        zip_code: savedAddress.zip_code,
        city: savedAddress.city,
        country: savedAddress.country,
        latitude: savedAddress.latitude,
        longitude: savedAddress.longitude,
      });

      navigate("/contract-creation");
    } catch (error) {
      console.error("Error saving location:", error);
      setSaveStatus(
        "Fehler beim Speichern des Standorts. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <NavbarSidebarLayout pageTitle="Standort">
      <div className="p-4">
        <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex lg:mt-1.5">
          <div className="mb-1 w-full">
            <div className="mb-4">
              <Breadcrumb className="mb-4">
                <BreadcrumbItem href="#">
                  <div className="flex items-center gap-x-3">
                    <HiHome className="text-xl" />
                    <span className="dark:text-white">Startseite</span>
                  </div>
                </BreadcrumbItem>
                <BreadcrumbItem>Standort</BreadcrumbItem>
              </Breadcrumb>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                Standort
              </h1>
            </div>
          </div>
        </div>
        <div className="mt-4 flex min-h-[600px] flex-col gap-4 lg:flex-row lg:items-stretch">
          <div className="flex w-full flex-col space-y-4 lg:w-2/5">
            <LocationActions
              currentLocation={currentLocation}
              setCurrentLocation={setCurrentLocation}
              setError={setError}
              userId={user?.id}
            />
            <AddressForm
              addressDetails={addressDetails}
              onInputChange={handleInputChange}
              onSave={handleSave}
              saveStatus={saveStatus}
              setCurrentLocation={setCurrentLocation}
              currentLocation={currentLocation}
              isSaving={isSaving}
            />
          </div>

          <div className="flex w-full lg:w-3/5">
            <div className="h-full w-full">
              <LocationMap
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                setError={setError}
                currentLocation={currentLocation}
                onAddressDetailsFetched={setAddressDetails}
                addressSource={addressDetails.source}
              />
            </div>
          </div>
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default LocationPage;
